/* eslint-disable max-lines */
import { CSSProperties, JSX } from 'react';

import { useViewportState } from './use-viewport-state';
import { ViewportServiceClass } from '../../viewport-service';
import { ViewportActions, ViewportConfig } from '../../types';

export interface ShowViewportControlsProps {
	viewportService: ViewportServiceClass;
	actions: ViewportActions;
	config: ViewportConfig;
	autoLevelText: boolean;
	setAutoLevelText: (autoLevelText: boolean) => void;
}
/* eslint-disable max-lines-per-function */
export const ShowViewportControls = (props: ShowViewportControlsProps): JSX.Element => {
	const { viewportService, actions, autoLevelText, setAutoLevelText } = props;
	const { viewportState, isZooming, isRotating, isPanning } = useViewportState(viewportService);

	const controlStyle: CSSProperties = {
		position: 'absolute',
						top: '20px',
						left: '20px',
						zIndex: 10,
						maxWidth: '400px',
						backgroundColor: 'rgba(255, 255, 255, 0.55)',
						border: '1px solid #ccc',
						borderRadius: '8px',
						padding: '15px',
						boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
	};

	return (<div style={controlStyle}>
		<h3 style={{ margin: '0 0 15px 0', fontSize: '16px' }}>Viewport Controls</h3>

		{/* Zoom Controls */}
		<div style={{ marginBottom: '15px' }}>
			<h4 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>Zoom</h4>
			<div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
				<button
					onClick={() => {
						try {
							if (actions && typeof actions.smoothZoom === 'function') {
								actions.smoothZoom(viewportState.zoom + viewportService.config.zoomStep);
							}
						} catch (error) {
							console.warn('Zoom in failed:', error);
						}
					}}
					disabled={isZooming}
					style={{ padding: '6px 12px', fontSize: '12px' }}
				>
					Zoom In
				</button>
				<button
					onClick={() => {
						try {
							if (actions && typeof actions.smoothZoom === 'function') {
								actions.smoothZoom(viewportState.zoom - viewportService.config.zoomStep);
							}
						} catch (error) {
							console.warn('Zoom out failed:', error);
						}
					}}
					disabled={isZooming}
					style={{ padding: '6px 12px', fontSize: '12px' }}
				>
					Zoom Out
				</button>
				<button
					onClick={() => {
						try {
							if (actions && typeof actions.smoothZoom === 'function') {
								actions.smoothZoom(1);
							}
						} catch (error) {
							console.warn('Reset zoom failed:', error);
						}
					}}
					disabled={isZooming}
					style={{ padding: '6px 12px', fontSize: '12px' }}
				>
					Reset Zoom
				</button>
			</div>
			<div style={{ fontSize: '12px', color: '#666' }}>
				{isZooming && <span style={{ color: 'blue' }}>🔄 Zooming...</span>}
				Current: {(viewportState.zoom * 100).toFixed(0)}%
			</div>
			<div style={{ marginTop: '10px' }}>
				<button
					onClick={() => {
						try {
							if (actions && typeof actions.fitToView === 'function') {
								actions.fitToView(); // No parameters = fit to current content
							}
						} catch (error) {
							console.warn('Fit to content failed:', error);
						}
					}}
					disabled={isZooming}
					style={{
						padding: '6px 12px',
						fontSize: '12px',
						backgroundColor: '#4CAF50',
						color: 'white',
						border: 'none',
						borderRadius: '4px',
						cursor: 'pointer'
					}}
				>
					🔍 Fit to Content
				</button>
			</div>
		</div>

		{/* Pan Controls */}
		<div style={{ marginBottom: '15px', paddingTop: '15px', borderTop: '1px solid #eee' }}>
			<h4 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>Pan</h4>
			<div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
				<button
					onClick={() => {
						try {
							if (actions && typeof actions.smoothPan === 'function') {
								actions.smoothPan(viewportState.xPan - 12, viewportState.yPan);
							}
						} catch (error) {
							console.warn('Pan left failed:', error);
						}
					}}
					style={{ padding: '6px 12px', fontSize: '12px' }}
				>
					← Left
				</button>
				<button
					onClick={() => {
						try {
							if (actions && typeof actions.smoothPan === 'function') {
								actions.smoothPan(viewportState.xPan + 12, viewportState.yPan);
							}
						} catch (error) {
							console.warn('Pan right failed:', error);
						}
					}}
					style={{ padding: '6px 12px', fontSize: '12px' }}
				>
					→ Right
				</button>
				<button
					onClick={() => {
						try {
							if (actions && typeof actions.smoothPan === 'function') {
								actions.smoothPan(viewportState.xPan, viewportState.yPan - 12);
							}
						} catch (error) {
							console.warn('Pan up failed:', error);
						}
					}}
					style={{ padding: '6px 12px', fontSize: '12px' }}
				>
					↑ Up
				</button>
				<button
					onClick={() => {
						try {
							if (actions && typeof actions.smoothPan === 'function') {
								actions.smoothPan(viewportState.xPan, viewportState.yPan + 12);
							}
						} catch (error) {
							console.warn('Pan down failed:', error);
						}
					}}
					style={{ padding: '6px 12px', fontSize: '12px' }}
				>
					↓ Down
				</button>
				<button
					onClick={() => {
						try {
							if (actions && typeof actions.smoothPan === 'function') {
								actions.smoothPan(0, 0);
							}
						} catch (error) {
							console.warn('Center view failed:', error);
						}
					}}
					style={{ padding: '6px 12px', fontSize: '12px' }}
				>
					Center View
				</button>
			</div>
			<div style={{ fontSize: '12px', color: '#666' }}>
				{isPanning && <span style={{ color: 'blue' }}>🔄 Panning...</span>}
				Current: {viewportState.xPan.toFixed(2)}, {viewportState.yPan.toFixed(2)}
			</div>
		</div>

		{/* Rotation Controls */}
		<div style={{ marginBottom: '15px', paddingTop: '15px', borderTop: '1px solid #eee' }}>
			<h4 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>Rotation</h4>

			{/* Auto-Level Text Toggle */}
			<div style={{ marginBottom: '10px', fontSize: '12px' }}>
				<label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
					<input
						type="checkbox"
						checked={autoLevelText}
						onChange={(e) => setAutoLevelText(e.target.checked)}
						style={{ margin: 0 }}
					/>
					Auto-level text (keep text level to viewer)
				</label>
			</div>
			<div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
				<button
					onClick={() => {
						try {
							if (actions && typeof actions.smoothRotation === 'function') {
								actions.smoothRotation(viewportState.rotation + 90);
							}
						} catch (error) {
							console.warn('Rotation +15 failed:', error);
						}
					}}
					disabled={isRotating}
					style={{ padding: '6px 12px', fontSize: '12px' }}
				>
					+15°
				</button>
				<button
					onClick={() => {
						try {
							if (actions && typeof actions.smoothRotation === 'function') {
								actions.smoothRotation(viewportState.rotation - 15);
							}
						} catch (error) {
							console.warn('Rotation -15 failed:', error);
						}
					}}
					disabled={isRotating}
					style={{ padding: '6px 12px', fontSize: '12px' }}
				>
					-15°
				</button>
				<button
					onClick={() => {
						try {
							if (actions && typeof actions.smoothRotation === 'function') {
								actions.smoothRotation(0);
							}
						} catch (error) {
							console.warn('Rotation reset failed:', error);
						}
					}}
					disabled={isRotating}
					style={{ padding: '6px 12px', fontSize: '12px' }}
				>
					Reset
				</button>
				{isRotating && (
					<button
						onClick={() => {
							try {
								if (actions && typeof actions.cancelRotationAnimation === 'function') {
									actions.cancelRotationAnimation();
								}
							} catch (error) {
								console.warn('Cancel rotation failed:', error);
							}
						}}
						style={{ padding: '6px 12px', fontSize: '12px', backgroundColor: '#ff6b6b', color: 'white' }}
					>
						Cancel
					</button>
				)}
			</div>

			<div style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '12px' }}>
				<label>
					Rotation: <input
						type="range"
						min="-180"
						max="180"
						step="1"
						value={viewportState.rotation}
						onChange={(e) => {
							try {
								if (actions && typeof actions.smoothRotation === 'function') {
									actions.smoothRotation(parseFloat(e.target.value));
								}
							} catch (error) {
								console.warn('Rotation slider failed:', error);
							}
						}}
						style={{ width: '80px' }}
					/>
					{viewportState.rotation.toFixed(0)}°
					{isRotating && <span style={{ color: 'blue', fontSize: '10px', marginLeft: '8px' }}>🔄</span>}
				</label>
			</div>
		</div>

		{/* Viewport Actions */}
		<div style={{ paddingTop: '15px', borderTop: '1px solid #eee' }}>
			<button
				onClick={() => {
					try {
						if (actions && typeof actions.smoothZoom === 'function' &&
							typeof actions.smoothPan === 'function' &&
							typeof actions.smoothRotation === 'function') {
							// Use smooth transitions to reset view
							actions.smoothZoom(1, 500); // Reset zoom to 1x over 500ms
							actions.smoothPan(0, 0, 500); // Reset pan to (0,0) over 500ms
							actions.smoothRotation(0, 500); // Reset rotation to 0° over 500ms
						} else if (actions && typeof actions.resetView === 'function') {
							// Fallback to direct reset if smooth functions aren't available
							actions.resetView();
						}
					} catch (error) {
						console.warn('Reset view failed:', error);
					}
				}}
				style={{ padding: '8px 16px', fontSize: '12px', width: '100%' }}
			>
				Reset View
			</button>
		</div>
	</div>);
};
